import { cn } from "@/lib/utils";
import React from "react";

/**
 * EmberWordmark — the `.person` logo as an inline SVG.
 *
 * Geometry (JetBrains Mono metrics at font-size=100 SVG units):
 *   baseline          y=100
 *   x-height top      y=47   (x-height = 53)
 *   cap-height top    y=27
 *   descender bottom  y=128
 *
 * The orange dot is a circle whose diameter exactly matches the x-height
 * of "person" (53 units, r=26.5), vertically centred on the x-height
 * band (cy=73.5) so it sits flush with the letter bodies. The "person"
 * text uses JetBrains Mono and inherits `currentColor` so the mark
 * works on both light and dark surfaces; the dot is always the fixed
 * Claude-orange (#D97757).
 *
 * Rendering: the SVG's height is set to 1em, so the parent's
 * `font-size` controls the mark's rendered size. `verticalAlign: baseline`
 * + a small tweak keeps it aligned with adjacent text when used inline.
 */
export function EmberWordmark({
  className,
  title,
}: {
  className?: string;
  title?: string;
}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 40 432 92"
      role="img"
      aria-label={title ?? ".person"}
      className={cn("inline-block select-none", className)}
      style={{ height: "1em", width: "auto", verticalAlign: "-0.12em" }}
    >
      <title>{title ?? ".person"}</title>
      {/* Dot — diameter = x-height, centred on the x-height band */}
      <circle cx="29.5" cy="73.5" r="26.5" fill="#D97757" />
      {/* Wordmark — baseline at y=100, font-size 100, JetBrains Mono */}
      <text
        x="68"
        y="100"
        fontFamily='"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace'
        fontSize="100"
        fontWeight="500"
        fill="currentColor"
      >
        person
      </text>
    </svg>
  );
}

export function MetadataStrip({ version, status, licence, stewardship, className }: { version: string; status: string; licence: string; stewardship: string; className?: string }) {
  return (
    <div className={cn("font-mono text-[10px] text-ink-3 uppercase tracking-widest py-3 flex flex-wrap gap-x-8 gap-y-2", className)}>
      <span>V_{version}</span>
      <span>STS_{status}</span>
      <span>LIC_{licence}</span>
      <span>{stewardship}</span>
    </div>
  );
}

export function HashSignature({ hash, shortHash, href = "#", className }: { hash: string; shortHash?: string; href?: string; className?: string }) {
  const displayHash = shortHash || hash.substring(0, 12) + "...";
  return (
    <a href={href} className={cn("font-mono text-[10px] text-ink-3 hover:text-ink transition-colors uppercase tracking-widest", className)}>
      sha256 &middot; {displayHash}
    </a>
  );
}

export function DotBreadcrumb({ segments, className }: { segments: { label: string; href?: string }[]; className?: string }) {
  return (
    <nav className={cn("flex items-center gap-3 font-mono text-sm text-ink-2", className)}>
      {segments.map((seg, idx) => (
        <div key={idx} className="flex items-center gap-3">
          {idx > 0 && <span className="text-hairline">/</span>}
          <div className="flex items-center gap-1.5 hover:text-ink transition-colors cursor-pointer">
            <span className="text-ember">.</span>
            <span>{seg.label}</span>
          </div>
        </div>
      ))}
    </nav>
  );
}

export function HairlineDivider({ className }: { className?: string }) {
  return <hr className={cn("border-t-[0.5px] border-hairline w-full my-8", className)} />;
}

export function MonoPill({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full border border-hairline font-mono text-[11px] uppercase tracking-wider text-ink-2 bg-paper-2", className)}>
      {children}
    </span>
  );
}
